/* eslint-disable no-unused-expressions, @typescript-eslint/no-unused-expressions */
import './kp-pdf-viewer-control-panel.less';

import type {PdfViewerFit} from '../kp-pdf-viewer/kp-pdf-viewer.component';
import type {Component} from 'typings/portaro.fe.types';
import type {IAugmentedJQuery, IDoCheck, IOnDestroy, IOnInit, IPostLink, IScope, ITranscludeFunction} from 'angular';
import type KpPdfViewerControlPanelService from './kp-pdf-viewer-control-panel.service';
import type PdfProxiedCommands from './pdf-proxied-commands';

const DEFAULT_SETTINGS = {
    minScale: 0.25,
    maxScale: 4,
    scaleStep: 0.25
};

interface ControlPanelScope extends IScope {
    $currentPage: number | string;
    $pageCount: number;
}

export abstract class KpPdfViewerControlPanelComponentController implements KpPdfViewerControlPanelComponentBindings, KpPdfViewerControlPanelComponentCommands, IOnInit, IPostLink, IDoCheck, IOnDestroy {
    public pdfControlId: string;
    public config: KpPdfViewerControlPanelSettings;

    public abstract onCurrentPageChange(locals: { $currentPage: number });

    public abstract onScaleChange(locals: { $scale: number });

    public abstract onFitChange(locals: { $fit: PdfViewerFit });

    public currentPage: number;
    public scale: number;
    public fit: PdfViewerFit;
    private transcludedScope: ControlPanelScope;
    private pdfViewer: PdfProxiedCommands;

    /*@ngInject*/
    constructor(private pdfViewerControlPanelService: KpPdfViewerControlPanelService,
                private $transclude: ITranscludeFunction,
                private $element: IAugmentedJQuery,
                private $scope: IScope) {
    }

    public $onInit(): void {
        this.config = Object.assign({}, DEFAULT_SETTINGS, this.config);

        if (this.pdfControlId) {
            this.pdfViewer = this.pdfViewerControlPanelService.registerPdfControlPanelController(this.pdfControlId, this);
        }
    }

    public $postLink(): void {
        // @ts-ignore we add missing properties later?
        this.transcludedScope = this.$scope.$new(false, this.$scope);

        this.transcludedScope.$currentPage = this.currentPage;

        this.$transclude(this.transcludedScope, (content) => {
            this.$element.append(content);
        });
    }

    public $doCheck(): void {
        if (this.transcludedScope && this.transcludedScope.$currentPage !== this.currentPage) {
            this.currentPageChange(parseInt(this.transcludedScope.$currentPage as string, 10));
        }
    }

    public $onDestroy(): void {
        this.pdfViewerControlPanelService.unregisterPdfControlPanelController(this.pdfControlId);
    }

    public currentPageChange(currentPage: number) {
        this.setCurrentPage(currentPage);
        this.onCurrentPageChange && this.onCurrentPageChange({$currentPage: currentPage});
        this.callPdfViewer((pdfViewer) => pdfViewer.setCurrentPage(currentPage));
    }

    public zoomIn() {
        this.updateScale(this.scale + this.config.scaleStep > this.config.maxScale ? this.config.maxScale : this.scale + this.config.scaleStep);
    }

    public zoomOut() {
        this.updateScale(this.scale - this.config.scaleStep < this.config.minScale ? this.config.minScale : this.scale - this.config.scaleStep);
    }

    public fitAuto() {
        this.fit = 'auto';
        this.onFitChange && this.onFitChange({$fit: this.fit});
        this.callPdfViewer((pdfViewer) => pdfViewer.fitAuto());
    }

    public fitWidth() {
        this.fit = 'width';
        this.onFitChange && this.onFitChange({$fit: this.fit});
        this.callPdfViewer((pdfViewer) => pdfViewer.fitWidth());
    }

    public updateScale(scale: number) {
        if (this.scale === scale) {
            return;
        }

        this.scale = scale;
        this.onScaleChange && this.onScaleChange({$scale: scale});
        this.callPdfViewer((pdfViewer) => pdfViewer.updateScale(scale));
    }

    public setCurrentPage(currentPage: number) {
        this.currentPage = this.transcludedScope.$currentPage = currentPage;
    }

    public setPageCount(totalPages: number) {
        this.transcludedScope.$pageCount = totalPages;
    }

    private callPdfViewer(callback: (pdfViewer?: PdfProxiedCommands) => any) {
        if (this.pdfControlId) {
            return callback(this.pdfViewer);
        }
    }
}

/**
 * @ngdoc component
 * @name kpPdfViewerControlPanel
 * @module portaro.components.kp-pdf-viewer
 *
 * @param {number} [currentPage]
 * @param {number} [pageCount]
 * @param {number} [scale]
 * @param {PdfViewerFit} [fit]
 * @param {KpPdfViewerControlPanelSettings} [config]
 * @param {function()} [onCurrentPageChange]
 * @param {number} [onCurrentPageChange.$currentPage]
 * @param {function()} [onScaleChange]
 * @param {number} [onScaleChange.$scale]
 * @param {function()} [onFitChange]
 * @param {PdfViewerFit} [onFitChange.$fit]
 *
 * @description
 *
 */
export default class KpPdfViewerControlPanelComponent implements Component<KpPdfViewerControlPanelComponentBindings> {
    public static componentName = 'kpPdfViewerControlPanel';

    public transclude = true;

    public bindings = {
        pdfControlId: '@?',
        onCurrentPageChange: '&?',
        onScaleChange: '&?',
        onFitChange: '&?'
    } as const;

    public controller = KpPdfViewerControlPanelComponentController;
}

export interface KpPdfViewerControlPanelComponentBindings {
    pdfControlId?: string;

    config?: KpPdfViewerControlPanelSettings;

    onCurrentPageChange(locals: { $currentPage: number });

    onScaleChange(locals: { $scale: number });

    onFitChange(locals: { $fit: PdfViewerFit });
}

export interface KpPdfViewerControlPanelSettings {
    minScale?: number;
    maxScale?: number;
    scaleStep?: number;
}

export interface KpPdfViewerControlPanelComponentCommands {
    zoomIn();

    zoomOut();

    fitAuto();

    fitWidth();
}
